import { jsPDF } from "jspdf";

import { buildScriptDocx } from "./docx";

// A4 portrait in points, with the margin every text run respects.
const PAGE = { width: 595.28, height: 841.89 };
const MARGIN = 56;
const CONTENT_WIDTH = PAGE.width - MARGIN * 2;

/**
 * ASCII file stem for the downloads: "Agachamento: postura" → "agachamento-postura".
 */
export function scriptFileName(title, fallback = "roteiro") {
    const slug = String(title || "")
        .normalize("NFD")
        .replace(/[̀-ͯ]/g, "")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "")
        .slice(0, 60);
    return slug || fallback;
}

/**
 * Plain-text rendering for the clipboard.
 *
 * @param {object} script  { title, overview, blocks[{ title, minutes, script }] }
 * @param {{ minutes: (count: number) => string }} labels
 */
export function scriptToText(script, { minutes }) {
    const lines = [script.title, "", script.overview, ""];

    script.blocks.forEach((block, index) => {
        lines.push(`${index + 1}. ${block.title} (${minutes(block.minutes)})`);
        lines.push(block.script);
        lines.push("");
    });

    return lines.join("\n").trim();
}

export async function copyScript(script, { minutes }) {
    await navigator.clipboard.writeText(scriptToText(script, { minutes }));
}

// Hands a Blob to the browser as a download and releases it afterwards.
export function saveBlob(blob, fileName) {
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = fileName;
    anchor.rel = "noopener";
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
}

/**
 * Builds and saves the PDF with jsPDF's built-in Helvetica (WinAnsi covers
 * Portuguese accents). One text run per line so page breaks fall between
 * lines, never through them.
 *
 * @param {object} script
 * @param {object} options
 * @param {string}   options.subtitle  meta line under the title (section · playlist · ~N min)
 * @param {string}   options.footer    left-hand footer on every page
 * @param {string}   options.fileName  stem of the saved file (".pdf" added here)
 * @param {{ minutes: (count: number) => string, page: (page: number, total: number) => string }} options.labels
 */
export function downloadScriptPdf(script, { subtitle = "", footer = "", fileName = "roteiro", labels }) {
    buildScriptPdf(script, { subtitle, footer, labels }).save(`${fileName}.pdf`);
}

/** The same PDF as a Blob, for callers that upload the rendered file (Studio exports). */
export function renderScriptPdf(script, { subtitle = "", footer = "", labels }) {
    return buildScriptPdf(script, { subtitle, footer, labels }).output("blob");
}

/** The same .docx as a Blob, for callers that upload the rendered file (Studio exports). */
export function renderScriptDocx(script, { subtitle = "", footer = "", labels }) {
    return buildScriptDocx(script, { subtitle, footer, minutes: labels.minutes });
}

function buildScriptPdf(script, { subtitle, footer, labels }) {
    const doc = new jsPDF({ unit: "pt", format: "a4" });
    const bottom = PAGE.height - MARGIN - 20; // room for the footer line
    let y = MARGIN;

    const ensure = (height) => {
        if (y + height > bottom) {
            doc.addPage();
            y = MARGIN;
        }
    };

    const paragraph = (text, { size, style = "normal", color = 40, lineHeight, after = 0 }) => {
        doc.setFont("helvetica", style);
        doc.setFontSize(size);
        doc.setTextColor(color);
        const lines = doc.splitTextToSize(String(text ?? ""), CONTENT_WIDTH);
        for (const line of lines) {
            ensure(lineHeight);
            doc.text(line, MARGIN, y);
            y += lineHeight;
        }
        y += after;
    };

    paragraph(script.title, { size: 20, style: "bold", color: 20, lineHeight: 26, after: 4 });
    if (subtitle) paragraph(subtitle, { size: 10, color: 130, lineHeight: 14, after: 12 });
    paragraph(script.overview, { size: 11.5, color: 70, lineHeight: 17, after: 18 });

    script.blocks.forEach((block, index) => {
        // Keep a block's rule, meta and title on the same page as its first lines.
        ensure(72);
        doc.setDrawColor(225);
        doc.setLineWidth(0.5);
        doc.line(MARGIN, y, PAGE.width - MARGIN, y);
        y += 14;

        paragraph(`${String(index + 1).padStart(2, "0")}   ·   ${labels.minutes(block.minutes)}`, { size: 9, style: "bold", color: 150, lineHeight: 12, after: 2 });
        paragraph(block.title, { size: 13, style: "bold", color: 20, lineHeight: 18, after: 4 });
        paragraph(block.script, { size: 11, color: 45, lineHeight: 16.5, after: 16 });
    });

    const total = doc.getNumberOfPages();
    for (let page = 1; page <= total; page++) {
        doc.setPage(page);
        doc.setFont("helvetica", "normal");
        doc.setFontSize(9);
        doc.setTextColor(160);
        if (footer) doc.text(footer, MARGIN, PAGE.height - 32);
        doc.text(labels.page(page, total), PAGE.width - MARGIN, PAGE.height - 32, { align: "right" });
    }

    return doc;
}

/**
 * Builds and saves a .docx — the format Google Docs, Word and LibreOffice
 * all open with the content in place (a Drive upload converts it to a
 * Google Doc). Google offers no URL that creates a document with a body,
 * so a file is the only way to hand the script over without a Google sign-in.
 *
 * @param {object} script
 * @param {{ subtitle?: string, footer?: string, fileName?: string, labels: { minutes: (count: number) => string } }} options
 */
export function downloadScriptDocx(script, { subtitle = "", footer = "", fileName = "roteiro", labels }) {
    const blob = buildScriptDocx(script, { subtitle, footer, minutes: labels.minutes });
    saveBlob(blob, `${fileName}.docx`);
}
